import { lazy, Suspense, type ComponentType } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { AppShell } from "./ui/AppShell";
import { Fehlergrenze } from "./ui/Fehlergrenze";
import { Anmeldung } from "./seiten/Anmeldung";

/**
 * The route table.
 *
 * Flat and explicit rather than generated: the navigator in `AppShell` and this list
 * are the only two places that know the application's shape, and keeping them both
 * readable in one screen is worth more than saving the repetition.
 *
 * The public surface is exactly two routes, and it is deliberately that small: a
 * Hausverwaltung that cannot sign up has to be created from a terminal, which is
 * why this product could not be handed to a customer at all. `/registrierung`
 * creates an organisation, `/einladung/:token` lets an invited colleague in. Every
 * other route needs a session, because a rent roll has no anonymous view.
 *
 * Screens are loaded per route. Statically imported, the bundle reached 1,77 MB and
 * every user paid for all nineteen screens — including the Betriebskosten settlement
 * and the document archive — before the login form could paint. Splitting is what
 * keeps the promise that the UI reacts immediately: the shell and the screen actually
 * being opened are the only things on the critical path, and the rest arrives while
 * somebody reads the page they asked for.
 */

/**
 * Screens export a named component; `lazy` wants a default export.
 *
 * The constraint names only the export being loaded. Constraining the whole module
 * would fail on any screen that also exports a helper taking required props, which
 * most of them do.
 */
function seite<K extends string>(
  laden: () => Promise<{ [P in K]: ComponentType }>,
  name: K,
) {
  return lazy(async () => ({ default: (await laden())[name] }));
}

const Periodenlauf = seite(() => import("./seiten/Periodenlauf"), "Periodenlauf");
const Auswertungen = seite(() => import("./seiten/Auswertungen"), "Auswertungen");

const Liegenschaften = seite(() => import("./seiten/Liegenschaften"), "Liegenschaften");
const Objektakte = seite(() => import("./seiten/Objektakte"), "Objektakte");
const Bestandverhaeltnisse = seite(() => import("./seiten/Bestandverhaeltnisse"), "Bestandverhaeltnisse");
const Personen = seite(() => import("./seiten/Personen"), "Personen");

const Zinsliste = seite(() => import("./seiten/Zinsliste"), "Zinsliste");
const Eingangsrechnungen = seite(() => import("./seiten/Eingangsrechnungen"), "Eingangsrechnungen");
const Buchhaltung = seite(() => import("./seiten/Buchhaltung"), "Buchhaltung");
const OffenePosten = seite(() => import("./seiten/OffenePosten"), "OffenePosten");
const Mahnwesen = seite(() => import("./seiten/Mahnwesen"), "Mahnwesen");
const Abrechnungen = seite(() => import("./seiten/Abrechnungen"), "Abrechnungen");
const Bank = seite(() => import("./seiten/Bank"), "Bank");

const Schaeden = seite(() => import("./seiten/Schaeden"), "Schaeden");
const Schadensfall = seite(() => import("./seiten/Schadensfall"), "Schadensfall");
const Auftraege = seite(() => import("./seiten/Auftraege"), "Auftraege");
const Fristen = seite(() => import("./seiten/Fristen"), "Fristen");
const Dokumente = seite(() => import("./seiten/Dokumente"), "Dokumente");

const Verwaltung = seite(() => import("./seiten/Verwaltung"), "Verwaltung");
const Postausgang = seite(() => import("./seiten/Postausgang"), "Postausgang");
const Einrichtung = seite(() => import("./seiten/Einrichtung"), "Einrichtung");
const Import = seite(() => import("./seiten/Import"), "Import");
const Hilfe = seite(() => import("./seiten/Hilfe"), "Hilfe");

const Registrierung = seite(() => import("./seiten/Registrierung"), "Registrierung");
const Einladung = seite(() => import("./seiten/Einladung"), "Einladung");

export function App() {
  const { benutzer, bereit } = useAuth();

  // A blank frame beats a spinner that flashes for 80 ms on every load.
  if (!bereit) return null;

  if (!benutzer) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/registrierung" element={<Registrierung />} />
          <Route path="/einladung/:token" element={<Einladung />} />
          <Route path="*" element={<Anmeldung />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <AppShell>
      {/*
        Inside the shell, not around it: a screen that throws must leave the navigator
        and the assistant usable, which is the whole point of catching it here.

        The Suspense fallback is deliberately empty. A chunk on a warm cache resolves
        in single-digit milliseconds, and anything drawn for that long is a flash the
        eye reads as jank — AS-16 makes the same point about skeletons.
      */}
      <Fehlergrenze>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Periodenlauf />} />
            <Route path="/auswertungen" element={<Auswertungen />} />

            <Route path="/liegenschaften" element={<Liegenschaften />} />
            <Route path="/liegenschaften/:id" element={<Objektakte />} />
            <Route path="/bestandverhaeltnisse" element={<Bestandverhaeltnisse />} />
            <Route path="/personen" element={<Personen />} />

            <Route path="/vorschreibungen" element={<Zinsliste />} />
            <Route path="/eingangsrechnungen" element={<Eingangsrechnungen />} />
            <Route path="/buchhaltung" element={<Buchhaltung />} />
            <Route path="/offene-posten" element={<OffenePosten />} />
            <Route path="/mahnwesen" element={<Mahnwesen />} />
            <Route path="/abrechnungen" element={<Abrechnungen />} />
            <Route path="/bank" element={<Bank />} />

            <Route path="/schaeden" element={<Schaeden />} />
            <Route path="/schaeden/:id" element={<Schadensfall />} />
            <Route path="/auftraege" element={<Auftraege />} />
            <Route path="/fristen" element={<Fristen />} />
            <Route path="/dokumente" element={<Dokumente />} />
            <Route path="/postausgang" element={<Postausgang />} />

            <Route path="/verwaltung" element={<Verwaltung />} />
            <Route path="/einrichtung" element={<Einrichtung />} />
            <Route path="/import" element={<Import />} />
            <Route path="/hilfe" element={<Hilfe />} />

            {/*
              Wer bereits angemeldet ist und auf der Registrierung landet, will die
              Einrichtung fortsetzen und nicht ein zweites Konto anlegen.
            */}
            <Route path="/registrierung" element={<Navigate to="/einrichtung" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Fehlergrenze>
    </AppShell>
  );
}
